/**
 * Shipments Module - Entry Point
 * 
 * This module bundles shipment-related utilities and makes them available
 * to legacy JavaScript files (shipping.js) via webpack bundling.
 * 
 * Webpack will compile this to: public/assets/js/shipping.bundle.js
 * 
 * NOTE: Tippy.js, ClipboardJS, moment.js, and hotkeys-js are bundled here and exported globally.
 */

// Export utilities
export { formatTracking, unformatTracking } from './utils/tracking-formatter';
export { calculateDistance, generatePath, calculateStraightDistance } from './utils/map-geometry';
export { destroyClipboard, setTooltip, ClipboardJS } from './utils/clipboard';
export type { ClipboardInstance } from './utils/clipboard';
export { tippy, defaultTippyConfig, clipboardTippyConfig, htmlTippyConfig, lazyTippyConfig } from './utils/tippy-init';
export type { TippyInstance } from './utils/tippy-init';
export { 
  moment,
  getBusinessDaysDiff, 
  businessDiff, 
  formatUnixTimestamp, 
  fromNow, 
  getCurrentUnixTimestamp,
  getTimeInTimezone,
  formatTimeInTimezone
} from './utils/date-time';

// Export configuration
export { statusCodes, getStatusText, isInTransit, isException, isDelivered } from './config/status-codes';
export type { StatusCodeMap } from './config/status-codes';

// Export services
export { ShipmentMap, MarkerType } from './services/ShipmentMap';
export type { MapConfig } from './services/ShipmentMap';

// Make utilities available on window for legacy code
import { formatTracking } from './utils/tracking-formatter';
import { calculateDistance, generatePath, calculateStraightDistance } from './utils/map-geometry';
import { destroyClipboard, setTooltip, ClipboardJS } from './utils/clipboard';
import { tippy } from './utils/tippy-init';
import { ShipmentMap, MarkerType } from './services/ShipmentMap';
import { 
  moment,
  getBusinessDaysDiff, 
  businessDiff, 
  formatUnixTimestamp, 
  fromNow, 
  getCurrentUnixTimestamp,
  getTimeInTimezone,
  formatTimeInTimezone
} from './utils/date-time';
import { statusCodes, getStatusText, isInTransit, isException, isDelivered } from './config/status-codes';
import hotkeys from 'hotkeys-js';
import { setupHotkeys, cleanupHotkeys, configureHotkeysFilter, getCurrentScope, setScope } from './utils/hotkeys-setup';

// Export hotkeys for re-export
export { hotkeys };

declare global {
  interface Window {
    ShipmentUtils: {
      formatTracking: typeof formatTracking;
      calculateDistance: typeof calculateDistance;
      generatePath: typeof generatePath;
      calculateStraightDistance: typeof calculateStraightDistance;
      destroyClipboard: typeof destroyClipboard;
      setTooltip: typeof setTooltip;
      tippy: typeof tippy;
      ClipboardJS: typeof ClipboardJS;
      ShipmentMap: typeof ShipmentMap;
      MarkerType: typeof MarkerType;
      moment: typeof moment;
      getBusinessDaysDiff: typeof getBusinessDaysDiff;
      businessDiff: typeof businessDiff;
      formatUnixTimestamp: typeof formatUnixTimestamp;
      fromNow: typeof fromNow;
      getCurrentUnixTimestamp: typeof getCurrentUnixTimestamp;
      getTimeInTimezone: typeof getTimeInTimezone;
      formatTimeInTimezone: typeof formatTimeInTimezone;
      statusCodes: typeof statusCodes;
      getStatusText: typeof getStatusText;
      isInTransit: typeof isInTransit;
      isException: typeof isException;
      isDelivered: typeof isDelivered;
      hotkeys: typeof hotkeys;
      setupHotkeys: typeof setupHotkeys;
      configureHotkeysFilter: typeof configureHotkeysFilter;
      getCurrentScope: typeof getCurrentScope;
      setScope: typeof setScope;
    };
    tippy: typeof tippy;
    ClipboardJS: typeof ClipboardJS;
    moment: typeof moment;
    hotkeys: typeof hotkeys;
  }
}

// Attach to window object for shipping.js access
window.ShipmentUtils = {
  formatTracking,
  calculateDistance,
  generatePath,
  calculateStraightDistance,
  destroyClipboard,
  setTooltip,
  tippy,
  ClipboardJS,
  ShipmentMap,
  MarkerType,
  moment,
  getBusinessDaysDiff,
  businessDiff,
  formatUnixTimestamp,
  fromNow,
  getCurrentUnixTimestamp,
  getTimeInTimezone,
  formatTimeInTimezone,
  statusCodes,
  getStatusText,
  isInTransit,
  isException,
  isDelivered,
  hotkeys,
  setupHotkeys,
  configureHotkeysFilter,
  getCurrentScope,
  setScope
};

// Export tippy, ClipboardJS, moment, and hotkeys globally for HTML inline scripts
// This ensures they're available before shipping.js executes
window.tippy = tippy;
window.ClipboardJS = ClipboardJS;
window.moment = moment;
window.hotkeys = hotkeys;

// console.log('[Shipments Module] Utilities loaded');
